import { Component } from 'react';
import type { ReactNode } from 'react';

import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import classnames from 'classnames';
import debounce from 'lodash/debounce';

import withFeatureTests from '../../../hotels-utils/skyscanner-application/feature-tests';

import type { FeatureTestsType } from '../../../hotels-utils/skyscanner-application/types';

import STYLES from './CardLayout.module.scss';

const DEBOUNCE_TIME = 100;

type CardLayoutProps = {
  featureTests: FeatureTestsType;
  name?: ReactNode;
  stars?: ReactNode;
  badges?: ReactNode;
  distance?: ReactNode;
  seeDetails?: ReactNode;
  priceAndNumbers?: ReactNode;
  url?: string;
  onClick?: Function;
  pricePolicy?: ReactNode;
  stayPricesSummary?: ReactNode;
  reviewSummary?: ReactNode;
  AISummary?: ReactNode;
  getImageNode?: Function;
  confidentMsg?: ReactNode;
  shimmering?: boolean;
};

type CardLayoutState = {
  mouseStatus: string;
};

class CardLayout extends Component<CardLayoutProps, CardLayoutState> {
  static defaultProps = {
    name: undefined,
    stars: undefined,
    badges: undefined,
    distance: undefined,
    seeDetails: undefined,
    priceAndNumbers: undefined,
    url: undefined,
    onClick: undefined,
    pricePolicy: undefined,
    stayPricesSummary: undefined,
    reviewSummary: undefined,
    getImageNode: () => {},
    confidentMsg: undefined,
    shimmering: undefined,
    AISummary: undefined,
  };

  private expImageGalleryEnable: boolean;

  private debouncedMouseEnter: (event: string) => void;

  constructor(props: CardLayoutProps) {
    super(props);

    const { featureTests } = props;
    this.expImageGalleryEnable =
      featureTests && featureTests.variant('BD_show_image_gallery_dv', 'b');

    this.state = {
      mouseStatus: this.expImageGalleryEnable ? 'enter' : 'leave',
    };

    this.debouncedMouseEnter = () => {};
  }

  componentDidMount() {
    this.debouncedMouseEnter = debounce(this.mouseHover, DEBOUNCE_TIME);
  }

  mouseHover = (type: string) => {
    if (!this.expImageGalleryEnable) {
      this.setState({ mouseStatus: type });
    }
  };

  onMouseEnter = () => {
    this.debouncedMouseEnter('enter');
  };

  onMouseLeave = () => {
    this.debouncedMouseEnter('leave');
  };

  render() {
    const {
      AISummary,
      badges,
      confidentMsg,
      distance,
      featureTests,
      getImageNode,
      name,
      onClick,
      priceAndNumbers,
      pricePolicy,
      reviewSummary,
      seeDetails,
      shimmering,
      stars,
      stayPricesSummary,
      url,
    } = this.props;

    const { mouseStatus } = this.state;

    const expImageGalleryEnable =
      featureTests && featureTests.variant('BD_show_image_gallery_dv', 'b');

    return (
      <div
        className={STYLES.HotelCardsListChunk__card}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <div className={STYLES.CardLayoutWrapper}>
          <BpkCard
            className={classnames(STYLES.CardLayout)}
            padded={false}
            href={expImageGalleryEnable ? null : url}
            rel="nofollow"
            data-testid="hotel-card"
            onClick={() => {
              if (!expImageGalleryEnable && onClick !== null)
                onClick?.(true, null, true, 'card');
            }}
          >
            <div className={STYLES.CardLayout__colLeft}>
              {getImageNode?.(mouseStatus)}
            </div>

            <BpkCard
              padded={false}
              href={url}
              rel="nofollow"
              className={classnames(STYLES.CardLayout__colCard, {
                [STYLES.CardLayout__shimmerCardHeight]: shimmering,
              })}
            >
              <div className={STYLES.CardLayout__colRight}>
                <div className={STYLES.CardLayout__basicInformation}>
                  {name}
                  <div className={STYLES.CardLayout__stars}>{stars}</div>
                  <div className={STYLES.CardLayout__badge}>{badges}</div>
                </div>
                <div>
                  <div className={STYLES.CardLayout__distance}>{distance}</div>
                  {reviewSummary}
                  {confidentMsg && (
                    <div className={STYLES.CardLayout__confidentMsg}>
                      {confidentMsg}
                    </div>
                  )}
                </div>
                {AISummary}
              </div>
              <div className={STYLES.CardLayout__extraColRight}>
                <div className={STYLES.CardLayout__extraColRightRowCenter}>
                  {priceAndNumbers}
                  {stayPricesSummary}
                  {pricePolicy}
                </div>
                <div className={STYLES.CardLayout__extraColRightRowBottom}>
                  {seeDetails}
                </div>
              </div>
            </BpkCard>
          </BpkCard>
        </div>
      </div>
    );
  }
}

export default withFeatureTests(CardLayout);
